import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import moment from 'moment';
import pluralize from 'pluralize';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';

import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { Tab } from '@headlessui/react';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { RadioGroup } from '@headlessui/react';

import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateContent,
  EmptyStateDescription,
  EmptyStatePrimaryButton,
  EmptyStateTitle,
  EmptyStateSecondaryButton,
} from '@frontend/shared-ui-kit/v2/empty-state';

import {
  useActions,
  useAppState,
  Campaign,
  RoleNames,
  useEffects,
} from '@frontend/howl-web-app/overmind';
import { useMediaQuery } from '@frontend/shared-utils';
import { Search } from '../ui/search';
import FlatFeeTable from './flat-fee-table';
import Product from '../publisher/campaign-center/product';
import MockButton from '../misc/mocks-button';
import CampaignCenterWrapper from '../publisher/campaign-center/campaignCenterWrapper';
import Pagination from '../base/common/pagination';
import {
  CampaignBadges,
  FlatFeeGmvGoal,
  ViewGifts,
  ReservedButton,
  CampaignPill,
  AppliedButton,
  GcalButton,
} from '@frontend/howl-web-app/feature-publisher';
import { ReportDropdown } from '../../containers/report/ReportDropdown';
import { Button } from '@frontend/shared-ui-kit/button';
import {
  Select,
  SelectButton,
  SelectDropdown,
  SelectOptions,
} from '@frontend/shared-ui-kit/select';
import { Typography } from '@frontend/shared-ui-kit/typography';
import Icon from '../ui/icon';
import { getSkeletonCards } from '../ui/skeleton-loader';
import { ChartIcon, Flag } from '@frontend/howl-web-app/feature-main';
import { Permission } from '@frontend/howl-web-app/shared/components';
import { AppliedStatus } from './campaign-center-types';
import { Disclosure } from '@frontend/shared-ui-kit/disclosure';
import { Link as UILink } from '@frontend/shared-ui-kit/link';
import { pascal } from 'radash';
import { useQuery } from '@tanstack/react-query';

enum Eligibililty {
  eligibile = 'eligible',
  no_course_required = 'no_course_required',
}
const BASE_TAB_STYLE =
  'bg-white relative rounded-t-lg font-medium py-2 sm:py-4';
const ACTIVE_TAB_STYLE = `${BASE_TAB_STYLE} border-x border-t-[3px] border-t-violet-300 border-b-white border-x-neutral-gray-300 focus:outline-none`;
const INACTIVE_TAB_STYLE = `${BASE_TAB_STYLE} bg-deep-space-10 text-deep-space-100 text-normal border border-b-0 border-neutral-gray-300`;

const HBO_MAX_PERFORMANCE_STRATEGY_ID =
  process.env.NEXT_PUBLIC_APP_ENV === 'release' ? 7371 : 147604;

const CampaignDetails = (props: any) => {
  const router = useRouter();
  const [tabIndex, setTabIndex] = useState(0);
  const isMobile = useMediaQuery('(max-width: 640px)');

  const flatFeeTableRef = React.useRef(null);
  const [sortField, setSortField] = useState('month');
  const [sortDirection, setSortDirection] = useState('desc');

  const { merchantPreview, previewCampaignId, merchantId, flags } = props;
  const { sessionData } = useAppState().session;
  const [searchQuery, setSearchQuery] = useState('');
  const [showMocks, setShowMocks] = useState(false);
  const [inStockOnly] = useState(true);
  const [saleOnly, setSaleOnly] = useState(false);
  const [giftsOnly, setGiftsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const publisherId = parseInt(router.query.publisherId as any);
  const campaignId = parseInt(
    previewCampaignId ? previewCampaignId : (router.query.campaignId as any),
  );
  const { selectedOrgId } = useAppState().organization;
  const actions = useActions();

  const { fetchCampaignCartExclusions } = actions.creatorCartExclusions;
  const creatorCartExclusionsState = useAppState().creatorCartExclusions;
  const createCartExclusionStateData: any = creatorCartExclusionsState.data;
  const {
    fetchCampaign,
    formatCampaignDateInfo,
    fetchBrandProducts,
    clearSelectedCampaign,
    fetchGifts,
  } = merchantPreview
    ? actions.ecommCampaignCenter
    : actions.creatorCampaignCenter;
  const { flatFeeDetails } = useAppState().creatorCampaignCenter;
  const { isFlatFeeCampaign } = useEffects().creatorCampaignCenter;

  const { formatDate } = actions.date;
  const { setInitialReportParams } = actions.report;
  const today = formatDate({ dateObj: moment() });
  const { triggerSnackbar } = useActions().ui;

  const ecommCCState = useAppState().ecommCampaignCenter;
  const creatorCCState = useAppState().creatorCampaignCenter;

  const { products, productsTotalItems, feedbackStatus, giftInfo } =
    merchantPreview ? ecommCCState : creatorCCState;

  const { fetchFlatFeeDetails } = actions.creatorCampaignCenter;

  const { campaignProductsLoading } = creatorCCState;

  const saleOptions = [
    { label: 'Show All', value: 'show_all' },
    { label: 'On Sale Only', value: 'on_sale_only' },
  ];

  const giftOptions = [
    { label: 'Show All', value: 'show_all' },
    { label: 'Show Gifts', value: 'gifts_only' },
  ];

  const [saleSelection, setSaleSelection] = useState<any>({
    label: 'Sale Status',
    value: null,
  });

  const [giftingSelection, setGiftingSelection] = useState<any>({
    label: 'Gifting',
    value: null,
  });

  const tableColumns = [
    'product_name',
    'product_brand',
    'store_page_url',
    'image_url',
    'product_description',
    'product_price',
    'sale_price',
    'gift_available',
  ];

  useEffect(() => {
    if (router.isReady) {
      if (!merchantPreview) {
        fetchCampaignCartExclusions({
          publisher_id: publisherId,
          strategy_id: campaignId,
        });
      }
    }
    return () => {
      if (!merchantPreview) {
        clearSelectedCampaign();
      }
    };
  }, [router.isReady]);

  const {
    data: selectedCampaign,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['campaigns', publisherId, campaignId],
    queryFn: () => {
      const merchId = props.merchantId || (router.query.merchantId as string);
      const version = flags.apiRateVersion ?? 0;
      const strategyTargetingVersion = flags.apiStrategyTargetingVersion ?? 0;
      return fetchCampaign({
        publisherId,
        strategyIds: [campaignId],
        merchantId: parseInt(merchId),
        version: version,
        strategyTargetingVersion,
      });
    },
    refetchOnWindowFocus: false,
  });

  const showFlatFeeDetails =
    flags?.campaignFlatFeeTab &&
    flatFeeDetails?.has_flat_fee_details &&
    (!flags?.flatFee3 ||
      selectedCampaign?.applied_status === AppliedStatus.Confirmed ||
      selectedCampaign?.applied_status === AppliedStatus.Invited);

  useEffect(() => {
    if (
      router.isReady &&
      selectedCampaign &&
      selectedCampaign.strategy_type === 10 &&
      !merchantPreview
    ) {
      fetchFlatFeeDetails({
        publisherId,
        merchantId: selectedCampaign.merch_id,
        strategyId: router.query.campaignId as string,
        showMocks,
        sortField,
      });
    }
  }, [router.isReady, selectedCampaign, sortField, sortDirection, showMocks]);

  useEffect(() => {
    setSaleOnly(saleSelection.value == 'on_sale_only');
  }, [saleSelection]);

  useEffect(() => {
    setGiftsOnly(giftingSelection.value == 'gifts_only');
  }, [giftingSelection]);

  useEffect(() => {
    setPage(1);
    if (selectedCampaign) {
      const groupingVersion = flags.productGroupingVersion ?? -1;
      fetchBrandProducts({
        publisherId,
        merchantId: selectedCampaign.merch_id,
        searchQuery,
        page: 1,
        perPage,
        inStockOnly,
        saleOnly,
        giftsOnly,
        showMocks,
        strategyIdList: [campaignId].toString(),
        groupingVersion: groupingVersion,
      });
    }
  }, [searchQuery, perPage, flags]);

  useEffect(() => {
    getBrandProducts();
  }, [selectedCampaign, page, saleOnly, giftsOnly, showMocks, flags]);

  useEffect(() => {
    const campaign = selectedCampaign;
    if (campaign) {
      fetchGifts({
        publisherId,
        merchantId: campaign.merch_id,
        strategyId: campaignId.toString(),
        showMocks,
      }).catch((error: any) => {
        triggerSnackbar({
          content: 'There was an error loading data',
          color: 'systemError',
        });
        console.log(error.message);
      });
    }
    if (
      flags['lockedBrandOverlay'] &&
      selectedCampaign &&
      selectedCampaign.eligibility_status === Eligibililty.eligibile
    ) {
      router.push(
        `/publisher/${selectedOrgId}/campaign-center/brands/${selectedCampaign.merch_id}`,
      );
    }
  }, [selectedCampaign]);

  const getBrandProducts = () => {
    const groupingVersion = flags.productGroupingVersion ?? -1;
    const campaign = selectedCampaign;
    if (campaign) {
      fetchBrandProducts({
        publisherId,
        merchantId: campaign.merch_id,
        searchQuery,
        page,
        perPage,
        inStockOnly,
        showMocks,
        saleOnly,
        giftsOnly,
        strategyIdList: [campaignId].toString(),
        groupingVersion: groupingVersion,
      }).catch((error: any) => {
        triggerSnackbar({
          content: 'There was an error loading data',
          color: 'systemError',
        });
        console.log(error.message);
      });

      setInitialReportParams({
        columns: tableColumns,
        reportType: 'campaign-details-products',
        endpoint: `/api/v2/publishers/${publisherId}/brands/${campaign.merch_id}/products/`,
      });
    }
  };

  const getReportParams = (campaign: any) => {
    type reportParamsType = {
      strategy_ids: string;
      in_stock_only: boolean;
      sale_only: boolean;
      gift_only?: boolean;
      merch_id: number;
      search_query?: string;
    };
    const reportParams: reportParamsType = {
      strategy_ids: `${campaign.strategy_id}`,
      in_stock_only: inStockOnly,
      sale_only: saleOnly,
      merch_id: campaign.merch_id,
      gift_only: giftsOnly,
    };

    if (searchQuery) reportParams.search_query = searchQuery;

    return reportParams;
  };

  const skeletonLoaders = (
    <>
      {getSkeletonCards(4, {
        className: 'ml-4 !w-56 py-2 px-4',
        imageShape: 'square',
        orientation: 'vertical',
        imageSize: 'h-56 w-full',
        textCount: 2,
        border: true,
        lineLength: 'w-48',
        textClassName: 'mt-2 item-left',
      })}
    </>
  );

  const totalPages = Math.ceil(productsTotalItems / perPage);
  const formattedDates =
    selectedCampaign && formatCampaignDateInfo(selectedCampaign);

  const handleSeeMore = () => {
    window.scrollTo(0, 0);
    setPage(page + 1);
  };

  const formatStrategyDescription = (rawDescription: string) => {
    if (!rawDescription) {
      return '';
    }
    const description = rawDescription
      .replace(/\n/g, ' <br/>')
      .split(' ')
      .map((string) => {
        let retString = '';
        if (string.startsWith('<br/>') && string.length > 5) {
          retString = '<br/>';
          string = string.substring(5);
        }

        return retString + string;
      })
      .join(' ');

    const sanitizedDescription = sanitizeHtml(description, {
      allowedTags: [
        'br',
        'a',
        'p',
        'li',
        'ul',
        'h3',
        'strong',
        'em',
        'b',
        'ol',
        'h1',
        'h2',
        'u',
      ],
      allowedClasses: {
        a: ['cursor-pointer', 'hover:underline', 'text-violet-300'],
        ul: ['list-disc', 'ml-8'],
        ol: ['list-decimal', 'ml-8'],
        h1: ['font-normal', 'text-[2rem]'],
        h2: ['font-normal', 'text-2xl'],
        h3: ['font-normal', 'text-xl'],
      },
      transformTags: {
        a: sanitizeHtml.simpleTransform('a', {
          class: 'cursor-pointer hover:underline text-violet-300',
          target: '_blank',
        }),
        ul: sanitizeHtml.simpleTransform('ul', {
          class: 'list-disc ml-8',
        }),
        ol: sanitizeHtml.simpleTransform('ol', {
          class: 'list-decimal ml-8',
        }),
        h1: sanitizeHtml.simpleTransform('h1', {
          class: 'font-normal text-[2rem]',
        }),
        h2: sanitizeHtml.simpleTransform('h2', {
          class: 'font-normal text-2xl',
        }),
        h3: sanitizeHtml.simpleTransform('h3', {
          class: 'font-normal text-xl',
        }),
      },
    });

    return sanitizedDescription;
  };

  const handleTabChange = (newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  const confirmedPill =
    selectedCampaign && isFlatFeeCampaign(selectedCampaign) ? (
      <div
        className={classNames('flex', {
          'justify-end': isMobile,
        })}
      >
        <CampaignPill
          campaign={selectedCampaign}
          className={'absolute top-2 right-2 !py-1 !px-3.5'}
        />
      </div>
    ) : null;

  const campaignHasExclusions =
    createCartExclusionStateData?.cart_exclusions &&
    Object.keys(createCartExclusionStateData.cart_exclusions).some(
      (exclusionType: string) =>
        createCartExclusionStateData.cart_exclusions[exclusionType].length > 0,
    );

  const campaignOverview = selectedCampaign ? (
    <>
      {confirmedPill}
      <div
        className={classNames('relative flex-1', {
          'p-4 sm:p-8': showFlatFeeDetails,
        })}
      >
        <div
          className={classNames('rounded-2xl mb-5', {
            'border-0 sm:border border-border-subtle p-4 sm:p-8 bg-white':
              !showFlatFeeDetails,
          })}
        >
          <Link
            href={`/publisher/${publisherId}/campaign-center/brands/${selectedCampaign.merch_id}/`}
            className="flex items-center text-violet-300 text-sm focus:underline hover:underline"
          >
            <div
              data-cy="merch_name"
              className="text-sm font-medium tracking-wider mb-1 uppercase text-violet-400"
            >
              {selectedCampaign.merch_name}
            </div>
          </Link>
          <div data-cy="strategy_name" className="text-2xl font-bold mb-4">
            {selectedCampaign.strategy_name}
          </div>
          {selectedCampaign.tagline && !merchantPreview && (
            <div className="font-normal italic text-sm line-clamp-3 my-4 deep-space-400">
              "{selectedCampaign.tagline}"
            </div>
          )}
          <div className={'break-words'}>
            <div
              data-cy="strategy_description"
              className="text-sm mb-6 break-words "
              dangerouslySetInnerHTML={{
                __html: formatStrategyDescription(
                  selectedCampaign.strategy_description as any,
                ),
              }}
            />
          </div>

          {isFlatFeeCampaign(selectedCampaign) && !merchantPreview && (
            <div className="text-sm mb-6 md:mr-40">
              <p className="font-medium">Flat Fee Details:</p>
              <p>You have agreed to participate in this Flat Fee campaign.</p>
              <p>
                For more information regarding rules and guidelines, please
                refer to your signed agreement.
              </p>
              <p>&nbsp;</p>
            </div>
          )}
          {giftInfo?.has_gift && (
            <ViewGifts merchantPreview={merchantPreview} />
          )}

          {!merchantPreview && campaignHasExclusions && (
            <div className="flex gap-1 mt-8">
              <div>Additional Rules Apply.</div>
              <Link
                href={`https://bit.ly/howl${
                  sessionData.publisher_type === 1 ? 'publisher' : 'creator'
                }rates${selectedCampaign.merch_id}`}
                target="_blank"
                className="underline cursor-pointer"
              >
                See Details.
              </Link>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-8 bg-white rounded-2xl border border-border-subtle">
          <div className="font-medium text-2xl text-black">Products</div>
          <div className="flex w-full flex-col sm:flex-row lg:flex-row md:flex-col justify-between mt-4">
            <div className="w-full self-center mr-2">
              <Search
                debounce={true}
                placeholder="Search for a product"
                onSearch={(query: any) => setSearchQuery(query)}
              />
            </div>
            <div className="flex mt-2 sm:mt-0 self-center">
              <div className="flex flex-row content-center">
                <div className="cursor-pointer mr-2">
                  <Select
                    data={saleOptions}
                    onChange={setSaleSelection}
                    selected={saleSelection}
                  >
                    <SelectButton
                      type="secondary"
                      labelClassName="whitespace-pre"
                    />
                    <SelectDropdown>
                      <SelectOptions />
                    </SelectDropdown>
                  </Select>
                </div>
                {!merchantPreview && selectedCampaign.has_gifting ? (
                  <div className="cursor-pointer mr-2">
                    <Select
                      data={giftOptions}
                      onChange={setGiftingSelection}
                      selected={giftingSelection}
                    >
                      <SelectButton type="secondary" />
                      <SelectDropdown>
                        <SelectOptions />
                      </SelectDropdown>
                    </Select>
                  </div>
                ) : null}
              </div>
              <div className="hidden h-10 w-10 md:flex md:mr-8 shrink-0">
                <ReportDropdown
                  circleButton
                  params={{
                    dateFrom: today,
                    dateTo: today,
                    page,
                    perPage,
                    reportParams: getReportParams(selectedCampaign),
                    additionalSlug: selectedCampaign.strategy_name,
                  }}
                  rowCount={productsTotalItems}
                  visibleRows={products ? products.length : 0}
                />
              </div>
            </div>
          </div>
          <div
            className="flex flex-wrap justify-start pt-5 mb-12 pb-12 sm:pb-0 gap-2 sm:gap-4"
            data-cy="all-products"
          >
            {campaignProductsLoading ? (
              skeletonLoaders
            ) : products.length ? (
              products.map((card, index) => (
                <Product
                  merchName={selectedCampaign.merch_name}
                  product={card}
                  key={index}
                  canMakeLink={!merchantPreview}
                  type="campaign"
                  fallbackImage={selectedCampaign.merchant_logo_url}
                  linkout={true}
                  noMargin={true}
                />
              ))
            ) : searchQuery || saleOnly ? (
              <div>No products found. Please refine your search.</div>
            ) : selectedCampaign.is_brand_url_targetting ? (
              <div>
                See campaign description for link to monetizable products.
              </div>
            ) : (
              <div>Products coming soon.</div>
            )}
            {products.length && page < totalPages ? (
              <button
                onClick={handleSeeMore}
                className="sm:hover:no-underline text-gray-700 mt-8 md:mb-12 md:mr-12 border border-gray-100 shadow-sm transition sm:hover:shadow-2xl transform sm:hover:-translate-y-3 flex relative flex-col items-center justify-center rounded-lg bg-white text-sm cursor-pointer overflow-hidden h-96 w-60 m-2'"
              >
                <div className="text-4xl text-gray-400 leading-10">
                  <span className="block mb-4">See</span>
                  <span className="block mb-4">More</span>
                  <span className="block mb-4">Products</span>
                </div>
                <ArrowRightIcon className="w-8 h-8 text-gray-400" />
              </button>
            ) : null}
          </div>
        </div>

        {(!merchantPreview || router.query.strategyId) && (
          <div className="fixed bottom-0 bg-white left-0 w-full z-20 shadow-elevation-high">
            <Pagination
              showPagination={true}
              currentPage={page}
              totalItems={productsTotalItems}
              perPage={perPage}
              eventPrefix="Creator Campaign Center Campaign Details Products"
              updatePage={(pageNum: number) => {
                window.scrollTo(0, 0);
                setPage(pageNum);
              }}
              updatePerPage={(perPageNum: number) => setPerPage(perPageNum)}
            />
          </div>
        )}
      </div>
    </>
  ) : null;

  const flatFeeGuidelines = selectedCampaign ? (
    <div className="relative flex-1 pb-8" data-cy="flat-fee-table">
      {confirmedPill}
      <FlatFeeTable
        selectedCampaign={selectedCampaign}
        sortField={sortField}
        setSortField={setSortField}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
      />
      <Flag name="campaignFlatFeeGmvGoal">
        <FlatFeeGmvGoal selectedCampaign={selectedCampaign} />
      </Flag>
    </div>
  ) : null;

  const content = selectedCampaign ? (
    <div data-cy="campaign-details" className="flex flex-col md:flex-row mt-4">
      <Sidebar
        campaign={selectedCampaign}
        formattedDates={formattedDates}
        publisherId={publisherId}
        feedbackStatus={feedbackStatus}
        campaignId={campaignId}
        showMocks={showMocks}
        merchantPreview={merchantPreview}
      />
      <div className={'overflow-hidden w-full'}>
        {showFlatFeeDetails ? (
          <div className="w-full" ref={flatFeeTableRef}>
            <Tab.Group onChange={handleTabChange} selectedIndex={tabIndex}>
              <Tab.List className="px-5 flex text-black gap-x-1 sm:gap-x-2 w-full">
                <Tab
                  className={({ selected }) =>
                    selected ? ACTIVE_TAB_STYLE : INACTIVE_TAB_STYLE
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className="px-2 sm:px-7">Campaign Overview</span>
                      {selected ? (
                        <div className="absolute -bottom-1 h-1 bg-white w-full"></div>
                      ) : null}
                    </>
                  )}
                </Tab>
                <div className="h-6 w-0 mt-3" />
                <Permission
                  roles={[
                    RoleNames.ACCOUNT_OWNER,
                    RoleNames.TEAM_ADMIN,
                    RoleNames.BILLING_ADMIN,
                  ]}
                >
                  <Tab
                    className={({ selected }) =>
                      selected ? ACTIVE_TAB_STYLE : INACTIVE_TAB_STYLE
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className="px-2 sm:px-7">Flat Fee Details</span>
                        {selected ? (
                          <div className="absolute -bottom-1 h-1 bg-white w-full"></div>
                        ) : null}
                      </>
                    )}
                  </Tab>
                </Permission>
              </Tab.List>
              <Tab.Panels
                className={classNames(
                  `rounded-lg bg-white text-black min-h-52 border-neutral-gray-300 border`,
                  {},
                )}
              >
                <Tab.Panel className="relative h-full">
                  {campaignOverview}
                </Tab.Panel>
                <Tab.Panel className="relative h-full">
                  {flatFeeGuidelines}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        ) : (
          campaignOverview
        )}
      </div>
    </div>
  ) : isLoading ? (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mt-4 sm:mt-8">
        <div className="lg:col-span-1  rounded-2xl flex flex-col gap-4">
          <div className="relative overflow-hidden h-80 w-full sm:h-72 sm:w-full rounded-2xl animate-pulse bg-neutral-opacity-100"></div>
          <div className="relative overflow-hidden h-52 w-full sm:w-full rounded-2xl animate-pulse bg-neutral-opacity-100"></div>
        </div>
        <div className="lg:col-span-3 flex flex-col gap-4">
          <div className="bg-white rounded-2xl border border-border-subtle h-72 p-4 sm:px-8 flex flex-col gap-6">
            {/* Skeleton loading lines */}
            <div className="h-8 w-24 animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
            <div className="h-full w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
          </div>
          <div className="bg-white rounded-2xl border border-border-subtle p-4 sm:px-8 flex flex-col gap-6">
            {/* Skeleton loading lines */}
            <div className="h-8 w-24 animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
            <div className="h-8 w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
            <div className="grid grid-cols-fill-64 gap-4">
              <div className="h-64 w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
              <div className="h-64 w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
              <div className="h-64 w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
              <div className="h-64 w-full animate-pulse bg-neutral-opacity-100 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : isError ? (
    <div className="mt-8">
      <EmptyState>
        <EmptyStateContent variant="column" className="space-y-2">
          <EmptyStateTitle>
            Oops! Something went wrong while loading this campaign.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Please try again later or contact support for assistance.
          </EmptyStateDescription>
        </EmptyStateContent>

        <EmptyStateContent variant="row" className="space-x-4 justify-center">
          <EmptyStateSecondaryButton
            onClick={() =>
              window.open(
                'https://help.planethowl.com/hc/en-us/requests/new',
                '_ blank',
              )
            }
          >
            Contact Support
          </EmptyStateSecondaryButton>
          <EmptyStatePrimaryButton
            onClick={() =>
              router.push(
                `/publisher/${selectedOrgId}/campaign-center/campaigns`,
              )
            }
          >
            Back to Campaigns
          </EmptyStatePrimaryButton>
        </EmptyStateContent>
      </EmptyState>
    </div>
  ) : (
    <div className="mt-8">
      <EmptyState>
        <EmptyStateContent variant="column" className="space-y-2">
          <EmptyStateTitle>
            Oops! This campaign is either no longer available or cannot be
            found.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Please check out our other campaigns.
          </EmptyStateDescription>
        </EmptyStateContent>

        <EmptyStateContent variant="row" className="space-x-4 justify-center">
          <EmptyStatePrimaryButton
            onClick={() =>
              router.push(
                `/publisher/${selectedOrgId}/campaign-center/campaigns`,
              )
            }
          >
            Back to Campaigns
          </EmptyStatePrimaryButton>
        </EmptyStateContent>
      </EmptyState>
    </div>
  );

  return (
    <>
      <MockButton showMocks={showMocks} setShowMocks={setShowMocks} />
      <CampaignCenterWrapper
        merchantPreview={merchantPreview}
        previewCampaignId={previewCampaignId}
        merchantId={merchantId}
        {...(flags.flatFee3 && isFlatFeeCampaign(selectedCampaign)
          ? { flatFee: true }
          : { campaign: true })}
        showShare={true}
        disableTabs={true}
      >
        {content}
      </CampaignCenterWrapper>
    </>
  );
};
const Sidebar = ({
  campaign,
  formattedDates,
  publisherId,
  feedbackStatus,
  campaignId,
  showMocks,
  merchantPreview,
}: any) => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const { trimCommission } = useEffects().creatorHomepage;

  const { giftInfo } = useAppState().creatorCampaignCenter;
  const flags = useFlags();
  const actions = useActions();
  const { updateFeedbackStatus, clearGiftInfo, clearBrandProducts } =
    actions.creatorCampaignCenter;
  const { sessionData } = useAppState().session;
  const { setAnalyticsAvailableStrategies } = useActions().publisher;
  const router = useRouter();
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [response, setResponse] = useState<'like' | 'dislike' | null>(null);
  const [modified, setModified] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [textReason, setTextReason] = useState<string>('');
  const { isFlatFeeCampaign, getFlatFeeRange, getFlatFeeVersion } =
    useEffects().creatorCampaignCenter;

  useEffect(() => {
    if (feedbackStatus) {
      setResponse(feedbackStatus.strategy_feedback_status);
    }
    return () => {
      clearBrandProducts();
      if (giftInfo) {
        clearGiftInfo();
      }
    };
  }, [feedbackStatus]);

  const statusOrder = ['like', 'dislike'];

  const likedReasons = [
    'Commission is high',
    "I'm interested in these products",
    'The campaign works with my schedule',
    'Other (tell us more below)',
  ];

  const dislikedReasons = [
    'Commission is too low',
    "I'm not interested in these products",
    "I don't have time to manage another campaign",
    'Other (tell us more below)',
  ];

  const getPriceModelAndAttributionWindowText = (campaign: Campaign) => {
    const pricingModel =
      campaign.strategy_rate_pricing_model === 'EPP' ? 'CPA+' : 'CPC';
    if (pricingModel === 'CPC') {
      return pricingModel;
    }
    // Custom logic for Samsung
    if (campaign.merch_id == 1242) {
      return `${campaign.attribution_window}`;
    }
    return `${pricingModel} ${campaign.attribution_window}`;
  };

  const reasons = response === 'like' ? likedReasons : dislikedReasons;

  function handleSubmit() {
    updateFeedbackStatus({
      publisherId,
      campaignId,
      response: statusOrder.indexOf(response as any) + 1,
      reason: (selectedReason as any) + 1 ? (selectedReason as any) + 1 : null,
      notes: textReason,
      showMocks,
    });
    setSubmitted(true);
  }

  const fieldClasses = {
    'flex flex-row justify-between items-start mt-4': isMobile,
    'flex flex-col mt-6': !isMobile,
  };

  return (
    <div className="md:mr-5 flex flex-col items-center sm:block">
      <div className="relative overflow-hidden h-80 w-80 sm:h-72 sm:w-72 rounded-2xl bg-white">
        {!merchantPreview && <CampaignBadges campaign={campaign} />}

        <img
          src={campaign.strategy_thumbnail}
          alt="strategy image"
          className="h-full w-full object-cover "
        />
      </div>
      <div className="border border-[#EAEBF0] rounded-2xl p-5 w-full sm:w-72 my-5 bg-white">
        <div className="flex flex-row justify-between items-center">
          <div className="uppercase text-sm font-medium tracking-wider">
            Campaign Details
          </div>
        </div>

        <Permission
          roles={[
            RoleNames.ACCOUNT_OWNER,
            RoleNames.BILLING_ADMIN,
            RoleNames.TEAM_ADMIN,
          ]}
        >
          {isFlatFeeCampaign(campaign) ? (
            <div className={classNames(fieldClasses)}>
              <div className="uppercase text-xs font-medium tracking-wider">
                Flat Fee
              </div>
              <Typography variant="h2" mobileVariant="h3" className="">
                {getFlatFeeRange(campaign)}
              </Typography>
            </div>
          ) : null}
        </Permission>

        <div className={classNames(fieldClasses)}>
          <div className="uppercase text-xs font-medium tracking-wider">
            Run Dates
          </div>
          <div className="text-sm ">
            <div data-cy="run-dates">{formattedDates.duration}</div>
            <div
              data-cy="days-left"
              className="text-[#FFAD40] text-right sm:text-left"
            >
              {formattedDates.daysText}
            </div>
          </div>
        </div>

        {giftInfo?.has_gift && !merchantPreview ? (
          <>
            <div className={classNames(fieldClasses)}>
              <div className="uppercase text-xs font-medium tracking-wider">
                Gifts Available
              </div>
              <div data-cy="flat-fee" className="text-sm ">
                {giftInfo.total_products}{' '}
                {pluralize('Product', giftInfo.total_products)}
              </div>
            </div>
          </>
        ) : null}
        {(!merchantPreview || sessionData.isAdmin) && (
          <>
            {getFlatFeeVersion(campaign) !== 2 ? (
              <Permission
                roles={[
                  RoleNames.ACCOUNT_OWNER,
                  RoleNames.BILLING_ADMIN,
                  RoleNames.TEAM_ADMIN,
                ]}
              >
                <div className={classNames(fieldClasses)}>
                  <div className="uppercase text-xs font-medium tracking-wider">
                    Commission Rate
                  </div>
                  <div
                    data-cy="strategy_rate"
                    className="text-sm font-bold sm:font-medium text-right sm:text-left"
                  >
                    {campaign.strategy_rate_range
                      ? campaign.strategy_rate_range.replace(/(\.00|\.99)/g, '')
                      : !campaign.strategy_rate
                      ? 'Not Currently Applicable'
                      : !merchantPreview ||
                        campaign.strategy_id === HBO_MAX_PERFORMANCE_STRATEGY_ID
                      ? trimCommission(campaign.strategy_rate, {
                          strategyId: campaign.strategy_id,
                        })
                      : trimCommission(campaign.strategy_rate)}
                  </div>
                </div>
              </Permission>
            ) : null}

            {isFlatFeeCampaign(campaign) ? (
              <div className={classNames(fieldClasses)}>
                <div className="uppercase text-xs font-medium tracking-wider">
                  Brand Approval
                </div>
                <div
                  data-cy="brand-approval-window"
                  className="text-sm w-32 sm:w-fit flex justify-right"
                >
                  {campaign.campaign_brief_url ? (
                    <a
                      href={campaign.campaign_brief_url}
                      className="text-system-message text-right"
                      target="_blank"
                      rel="noreferrer"
                    >
                      See Campaign Guidelines
                    </a>
                  ) : (
                    'None Required'
                  )}
                </div>
              </div>
            ) : null}

            {flags.flatFee3 && isFlatFeeCampaign(campaign) ? (
              <AppliedButton campaign={campaign} className="mt-6 !w-full" />
            ) : null}

            {getFlatFeeVersion(campaign) !== 2 && (
              <div className={classNames(fieldClasses)}>
                <div className="uppercase text-xs font-medium sm:font-normal">
                  Attribution
                </div>
                <div
                  data-cy="attribution-window"
                  className={classNames('text-sm flex flex-col', {
                    'font-bold w-36 justify-right text-right': isMobile,
                    'font-medium': !isMobile,
                  })}
                >
                  {getFlatFeeVersion(campaign) === 2 ? (
                    'Attribution Not Applicable'
                  ) : (
                    <>
                      {getPriceModelAndAttributionWindowText(campaign)}
                      {campaign.attribution_window_subtext && (
                        <div data-cy="attribution-window-subtext">
                          {campaign.attribution_window_subtext}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {getFlatFeeVersion(campaign) !== 2 && !merchantPreview ? (
              <Permission
                roles={[
                  RoleNames.ACCOUNT_OWNER,
                  RoleNames.BILLING_ADMIN,
                  RoleNames.TEAM_ADMIN,
                ]}
              >
                <a
                  onClick={() => {
                    router.push(
                      `/publisher/${router.query['publisherId']}/analytics/campaigns?merch_ids=${campaign.merch_id}&search=${campaign.strategy_name}`,
                    );
                    setAnalyticsAvailableStrategies(true);
                  }}
                >
                  <div className="text-violet-300 flex items-center cursor-pointer mt-8 text-xs">
                    <div className="w-6 h-6 mr-4">
                      <ChartIcon />
                    </div>
                    View Campaign Analytics
                  </div>
                </a>
              </Permission>
            ) : null}
          </>
        )}
      </div>
      {!merchantPreview && getFlatFeeVersion(campaign) !== 2 ? (
        <Permission
          roles={[
            RoleNames.ACCOUNT_OWNER,
            RoleNames.BILLING_ADMIN,
            RoleNames.TEAM_ADMIN,
          ]}
        >
          <>
            {flags.addToCal === 'gcal' ? (
              <GcalButton campaign={campaign} />
            ) : (
              <ReservedButton
                pubId={publisherId}
                campaign={campaign}
                iconClassName="h-6 w-6 pb-1 align-middle"
              />
            )}
          </>
        </Permission>
      ) : null}

      {campaign ? (
        <div className="w-full sm:w-72 my-4">
          <Disclosure
            title="FTC Disclosure Reminder"
            description={
              <span>
                To maintain consumer trust and comply with FTC guidelines,
                please use both #
                {campaign.merch_name ? pascal(campaign.merch_name) : ''}
                Partner and #affiliate every time you post links. Click{' '}
                <UILink
                  external={true}
                  href="https://help.planethowl.com/hc/en-us/articles/15763473852443"
                >
                  here
                </UILink>{' '}
                to learn more.
              </span>
            }
          />
        </div>
      ) : null}
    </div>
  );
};
export default withLDConsumer()(CampaignDetails);
